import { IConsigned, IConsignedProduct, TypeProduct } from '../interfaces';
import { Chip, useTheme } from '@mui/material';
import { formatCurrency } from 'app/utils/normalizer';
import dayjs from 'dayjs';
import {
  GridContainer,
  InfoItem,
  InfoItemTitle,
  StyledAvatar,
  StyledCardHeader,
  StyledCheckboxOption,
  StyledContent,
  StyledContentWrapper,
  StyledInfoChip,
  StyledOption,
  StyledOptionHeader,
  StyledWarningMessage,
} from '../ConsignedCard/ConsignedCard.styles';
import { CustomRadioInput } from 'app/components';
import { InfoOutlined } from '@mui/icons-material';

type TInssSemeaCardProps = {
  consigned?: IConsigned;
  setConsignedConfig: (value?: IConsigned) => void;
  consignedProduct?: IConsignedProduct;
  setConsignedProduct: (value?: IConsignedProduct) => void;
};

const formatOptionalCurrency = (value?: string | number) => {
  if (value === undefined || value === null || value === '') {
    return 'Não informado';
  }

  const parsed = typeof value === 'number' ? value : parseFloat(value);

  if (Number.isNaN(parsed)) {
    return 'Não informado';
  }

  return formatCurrency(parsed);
};

const formatOptionalDate = (value?: string) => {
  if (!value) {
    return 'Não informado';
  }

  return dayjs(value).format('DD/MM/YYYY');
};

const InssSemeaCard = ({
  consigned,
  setConsignedConfig,
  setConsignedProduct,
  consignedProduct,
}: TInssSemeaCardProps) => {
  const theme = useTheme();

  const handleSelectProduct = (product: IConsignedProduct) => {
    setConsignedProduct(product);
    setConsignedConfig(consigned);
  };

  return (
    <StyledContentWrapper>
      {consigned?.products?.map((product, key) => {
        const isLoanAuthorized: boolean =
          (product?.authorization?.details?.filter(
            (item) => item.type_product === TypeProduct.LOAN,
          )[0]?.authorized &&
            !!product.available_balance) ||
          false;
        const isSelected =
          product?.balance_check_id === consignedProduct?.balance_check_id;

        return (
          <div key={key}>
            <StyledOption isSelected={isSelected && !!isLoanAuthorized}>
              <StyledCardHeader>
                <StyledOptionHeader>
                  {consigned?.employee?.name || 'Nome indisponível'}
                </StyledOptionHeader>
                <StyledInfoChip>
                  <Chip
                    variant="outlined"
                    color={isLoanAuthorized ? 'success' : 'warning'}
                    label={
                      <StyledInfoChip>
                        <StyledAvatar
                          color={
                            isLoanAuthorized
                              ? (theme.palette.brand.success[500] as string)
                              : (theme.palette.brand.attention[500] as string)
                          }
                        />
                        {isLoanAuthorized ? 'Elegível' : 'Inelegível'}
                      </StyledInfoChip>
                    }
                  />
                  <StyledCheckboxOption>
                    <CustomRadioInput
                      isChecked={isSelected}
                      handleClick={() => handleSelectProduct(product)}
                      hasMargin
                      disabled={!isLoanAuthorized}
                    />
                  </StyledCheckboxOption>
                </StyledInfoChip>
              </StyledCardHeader>
              {consigned.meta_data.unauthorization_reason?.description && (
                <StyledWarningMessage>
                  <InfoOutlined
                    style={{
                      marginRight: '0.6rem',
                      color: theme.palette.brand.attention[700],
                    }}
                  />
                  <span>
                    Tomador inelegível:{' '}
                    {consigned.meta_data?.unauthorization_reason?.description ||
                      'Não foi possível informar o motivo.'}
                  </span>
                </StyledWarningMessage>
              )}
              <GridContainer>
                <StyledContent>
                  <InfoItemTitle>Número do benefício</InfoItemTitle>
                  <InfoItem>
                    {consigned?.employee?.code || 'Não informado'}
                  </InfoItem>
                </StyledContent>
                <StyledContent>
                  <InfoItemTitle>Código do benefício</InfoItemTitle>
                  <InfoItem>
                    {consigned?.meta_data?.benefit_type?.code ??
                      'Não informado'}
                  </InfoItem>
                </StyledContent>
                <StyledContent>
                  <InfoItemTitle>Tipo de benefício</InfoItemTitle>
                  <InfoItem>
                    {consigned?.meta_data?.benefit_type?.description ||
                      'Não informado'}
                  </InfoItem>
                </StyledContent>
                <StyledContent>
                  <InfoItemTitle>Representante legal</InfoItemTitle>
                  <InfoItem>
                    {consigned?.meta_data?.has_legal_representative === undefined
                      ? 'Não informado'
                      : consigned.meta_data.has_legal_representative
                      ? 'Sim'
                      : 'Não'}
                  </InfoItem>
                </StyledContent>
                <StyledContent>
                  <InfoItemTitle>Data do despacho do benefício</InfoItemTitle>
                  <InfoItem>
                    {formatOptionalDate(
                      consigned?.meta_data?.benefit_dispatch_date,
                    )}
                  </InfoItem>
                </StyledContent>
                <StyledContent>
                  <InfoItemTitle>CBC pagador</InfoItemTitle>
                  <InfoItem>
                    {consigned?.meta_data?.cbc_if_payer || 'Não informado'}
                  </InfoItem>
                </StyledContent>
                <StyledContent>
                  <InfoItemTitle>Valor líquido</InfoItemTitle>
                  <InfoItem>
                    {formatOptionalCurrency(consigned?.meta_data?.net_amount)}
                  </InfoItem>
                </StyledContent>
                <StyledContent>
                  <InfoItemTitle>Valor base da margem</InfoItemTitle>
                  <InfoItem>
                    {formatOptionalCurrency(product?.available_balance)}
                  </InfoItem>
                </StyledContent>
                <StyledContent>
                  <InfoItemTitle>Contratos ativos/suspensos</InfoItemTitle>
                  <InfoItem>
                    {consigned.meta_data?.active_suspended_loans_qty ??
                      consigned.meta_data?.amount_loans_assets_suspended ??
                      0}
                  </InfoItem>
                </StyledContent>
                <StyledContent>
                  <InfoItemTitle>Valor comprometido</InfoItemTitle>
                  <InfoItem>
                    {formatOptionalCurrency(
                      consigned?.meta_data?.committed_amount,
                    )}
                  </InfoItem>
                </StyledContent>
                <StyledContent>
                  <InfoItemTitle>Data do bloqueio do benefício</InfoItemTitle>
                  <InfoItem>
                    {formatOptionalDate(
                      consigned?.meta_data?.benefit_block_date,
                    )}
                  </InfoItem>
                </StyledContent>
                <StyledContent>
                  <InfoItemTitle>Motivo do bloqueio</InfoItemTitle>
                  <InfoItem>
                    {consigned?.meta_data?.block_type?.description ||
                      'Não informado'}
                  </InfoItem>
                </StyledContent>
              </GridContainer>
            </StyledOption>
          </div>
        );
      })}
    </StyledContentWrapper>
  );
};

export default InssSemeaCard;
